// src/store/leads/leadsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';
import { errorMessage } from '../errorMessage';

// Holds the pre-move { lifecycle_stage, lost_reason } for each in-flight
// moveStage request, keyed by the thunk's requestId, so a rejected move can
// restore the card to its previous state. Lives outside redux state because
// it's transient bookkeeping, not something the UI reads.
const pendingStageChanges = new Map();

// Async thunks
export const fetchPipeline = createAsyncThunk(
  'leads/fetchPipeline',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getLeadPipeline(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const moveStage = createAsyncThunk(
  'leads/moveStage',
  async ({ id, stage, lost_reason }, { rejectWithValue }) => {
    try {
      const response = await apiService.moveCustomerStage(id, { stage, lost_reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const assignOwner = createAsyncThunk(
  'leads/assignOwner',
  async ({ id, owner }, { rejectWithValue }) => {
    try {
      const response = await apiService.assignCustomerOwner(id, { owner });
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchAssignableUsers = createAsyncThunk(
  'leads/fetchAssignableUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getAssignableUsers();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

export const fetchCustomerOptions = createAsyncThunk(
  'leads/fetchCustomerOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getCustomerOptions();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  }
);

// Initial state
const initialState = {
  cards: [],
  assignableUsers: [],
  customerOptions: [],
  customerOptionsError: null,
  isLoading: false,
  isUsersLoading: false,
  isOptionsLoading: false,
  isSaving: false,
  error: null
};

// Create slice
const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearLeadsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchPipeline
      .addCase(fetchPipeline.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPipeline.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cards = action.payload;
      })
      .addCase(fetchPipeline.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // moveStage (optimistic)
      .addCase(moveStage.pending, (state, action) => {
        state.isSaving = true;
        state.error = null;
        const { id, stage, lost_reason } = action.meta.arg;
        const card = state.cards.find((c) => c.id === id);
        if (card) {
          pendingStageChanges.set(action.meta.requestId, {
            lifecycle_stage: card.lifecycle_stage,
            lost_reason: card.lost_reason
          });
          card.lifecycle_stage = stage;
          card.lost_reason = lost_reason ?? null;
        }
      })
      .addCase(moveStage.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.cards.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.cards[index] = action.payload;
        }
        pendingStageChanges.delete(action.meta.requestId);
      })
      .addCase(moveStage.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
        const previous = pendingStageChanges.get(action.meta.requestId);
        if (previous) {
          const card = state.cards.find((c) => c.id === action.meta.arg.id);
          if (card) {
            card.lifecycle_stage = previous.lifecycle_stage;
            card.lost_reason = previous.lost_reason;
          }
        }
        pendingStageChanges.delete(action.meta.requestId);
      })

      // assignOwner
      .addCase(assignOwner.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(assignOwner.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.cards.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.cards[index] = action.payload;
        }
      })
      .addCase(assignOwner.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })

      // fetchAssignableUsers
      .addCase(fetchAssignableUsers.pending, (state) => {
        state.isUsersLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignableUsers.fulfilled, (state, action) => {
        state.isUsersLoading = false;
        state.assignableUsers = action.payload;
      })
      .addCase(fetchAssignableUsers.rejected, (state, action) => {
        state.isUsersLoading = false;
        state.error = action.payload;
      })

      // fetchCustomerOptions
      .addCase(fetchCustomerOptions.pending, (state) => {
        state.isOptionsLoading = true;
        state.customerOptionsError = null;
      })
      .addCase(fetchCustomerOptions.fulfilled, (state, action) => {
        state.isOptionsLoading = false;
        state.customerOptions = action.payload;
      })
      .addCase(fetchCustomerOptions.rejected, (state, action) => {
        state.isOptionsLoading = false;
        state.customerOptionsError = action.payload;
      });
  }
});

export const { clearLeadsError } = leadsSlice.actions;
export default leadsSlice.reducer;
